/**
 * GrowthConsole - Analytics and growth metrics
 * Phase 11.5d - Growth Console
 */
import React, { useState, useCallback, useEffect } from 'react'
import {
  IoTrendingUp,
  IoTrendingDown,
  IoPeople,
  IoAnalytics,
  IoFunnel,
  IoCalendar,
  IoStatsChart,
  IoEye,
  IoRefresh,
} from 'react-icons/io5'
import { apiGetAuth } from '../../api'
import { AccordionSection } from './AccordionSection'
import { CompactCard, CompactCardGrid } from './CompactCard'

interface KPIs {
  users: {
    total: number
    active: number
    new_this_week: number
    new_this_month: number
  }
  engagement: {
    dau: number
    wau: number
    mau: number
    stickiness_percent: number
  }
  activation: {
    activated_users: number
    activation_rate_percent: number
  }
}

interface FunnelStage {
  stage: string
  count: number
  conversion: number
}

interface FunnelData {
  stages: FunnelStage[]
  overall_conversion_percent: number
}

interface EventSummary {
  summary: {
    total_events: number
    unique_sessions: number
    events_per_session: number
  }
  events_by_type: Array<{ event: string; count: number }>
  top_pages: Array<{ page: string; views: number }>
  top_referrers: Array<{ referrer: string; sessions: number }>
}

interface TrendPoint {
  date: string
  value: number
}

export function GrowthConsole() {
  const [kpis, setKpis] = useState<KPIs | null>(null)
  const [funnel, setFunnel] = useState<FunnelData | null>(null)
  const [events, setEvents] = useState<EventSummary | null>(null)
  const [dauTrend, setDauTrend] = useState<TrendPoint[]>([])
  const [signupsTrend, setSignupsTrend] = useState<TrendPoint[]>([])
  const [loading, setLoading] = useState(false)

  // Load all data
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [kpisRes, funnelRes, eventsRes, dauRes, signupsRes] = await Promise.all([
        apiGetAuth<KPIs>('/growth/kpis'),
        apiGetAuth<FunnelData>('/growth/funnel'),
        apiGetAuth<EventSummary>('/growth/events/summary?days=7'),
        apiGetAuth<{ data: TrendPoint[] }>('/growth/kpis/trend?metric=dau&days=14'),
        apiGetAuth<{ data: TrendPoint[] }>('/growth/kpis/trend?metric=signups&days=14'),
      ])
      setKpis(kpisRes || null)
      setFunnel(funnelRes || null)
      setEvents(eventsRes || null)
      setDauTrend(dauRes?.data || [])
      setSignupsTrend(signupsRes?.data || [])
    } catch (error) {
      console.error('Error loading growth data:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Simple sparkline component
  const Sparkline = ({ data, color = '#6366f1' }: { data: TrendPoint[]; color?: string }) => {
    if (data.length < 2) return null
    const values = data.map(d => d.value)
    const max = Math.max(...values)
    const min = Math.min(...values)
    const range = max - min || 1
    const width = 100
    const height = 30

    const points = values.map((v, i) => {
      const x = (i / (values.length - 1)) * width
      const y = height - ((v - min) / range) * height
      return `${x},${y}`
    }).join(' ')

    return (
      <svg width={width} height={height} className="sparkline">
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth={2}
        />
      </svg>
    )
  }

  // Calculate trend direction
  const getTrend = (data: TrendPoint[]): 'up' | 'down' | 'flat' => {
    if (data.length < 2) return 'flat'
    const recent = data.slice(-3).reduce((acc, d) => acc + d.value, 0) / 3
    const earlier = data.slice(0, 3).reduce((acc, d) => acc + d.value, 0) / 3
    if (recent > earlier * 1.1) return 'up'
    if (recent < earlier * 0.9) return 'down'
    return 'flat'
  }

  return (
    <div className="growth-console">
      {/* Refresh button */}
      <div className="growth-header">
        <button onClick={loadData} disabled={loading} className="growth-refresh-btn">
          <IoRefresh className={loading ? 'spinning' : ''} />
          Refresh
        </button>
      </div>

      {/* KPI Cards */}
      {kpis && (
        <CompactCardGrid columns={4}>
          <CompactCard
            label="Total Users"
            value={kpis.users.total.toLocaleString()}
            icon={<IoPeople size={20} />}
            status="neutral"
            trend={`+${kpis.users.new_this_week} this week`}
            trendDirection="up"
          />
          <CompactCard
            label="DAU"
            value={kpis.engagement.dau.toLocaleString()}
            icon={<IoAnalytics size={20} />}
            status="neutral"
            trendDirection={getTrend(dauTrend)}
          />
          <CompactCard
            label="WAU"
            value={kpis.engagement.wau.toLocaleString()}
            icon={<IoCalendar size={20} />}
            status="neutral"
          />
          <CompactCard
            label="Stickiness"
            value={`${kpis.engagement.stickiness_percent}%`}
            icon={<IoTrendingUp size={20} />}
            status={kpis.engagement.stickiness_percent > 20 ? 'healthy' : 'neutral'}
          />
        </CompactCardGrid>
      )}

      {/* Trends */}
      <AccordionSection title="Trends (14 days)" defaultOpen={true}>
        <div className="growth-trends">
          <div className="growth-trend-card">
            <div className="growth-trend-header">
              <span>Daily Active Users</span>
              {dauTrend.length > 0 && (
                <span className="growth-trend-latest">{dauTrend[dauTrend.length - 1]?.value}</span>
              )}
            </div>
            <Sparkline data={dauTrend} color="#10b981" />
          </div>
          <div className="growth-trend-card">
            <div className="growth-trend-header">
              <span>Daily Signups</span>
              {signupsTrend.length > 0 && (
                <span className="growth-trend-latest">{signupsTrend[signupsTrend.length - 1]?.value}</span>
              )}
            </div>
            <Sparkline data={signupsTrend} color="#6366f1" />
          </div>
        </div>
      </AccordionSection>

      {/* Conversion Funnel */}
      <AccordionSection title="Conversion Funnel (30 days)" defaultOpen={true}>
        {funnel && (
          <div className="growth-funnel">
            <div className="growth-funnel-visual">
              {funnel.stages.map((stage, i) => {
                const widthPercent = Math.max(20, stage.conversion)
                return (
                  <div
                    key={stage.stage}
                    className="growth-funnel-stage"
                    style={{ width: `${widthPercent}%` }}
                  >
                    <div className="growth-funnel-stage-bar" />
                    <div className="growth-funnel-stage-info">
                      <span className="growth-funnel-stage-name">{stage.stage}</span>
                      <span className="growth-funnel-stage-count">{stage.count.toLocaleString()}</span>
                      {i > 0 && (
                        <span className="growth-funnel-stage-conversion">
                          {stage.conversion}% from prev
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="growth-funnel-summary">
              <IoFunnel size={20} />
              <span>Overall conversion: <strong>{funnel.overall_conversion_percent}%</strong></span>
            </div>
          </div>
        )}
      </AccordionSection>

      {/* Events Summary */}
      <AccordionSection title="Events (7 days)" defaultOpen={false}>
        {events && (
          <div className="growth-events">
            <CompactCardGrid columns={3}>
              <CompactCard
                label="Total Events"
                value={events.summary.total_events.toLocaleString()}
                icon={<IoStatsChart size={20} />}
                status="neutral"
              />
              <CompactCard
                label="Unique Sessions"
                value={events.summary.unique_sessions.toLocaleString()}
                icon={<IoPeople size={20} />}
                status="neutral"
              />
              <CompactCard
                label="Events/Session"
                value={events.summary.events_per_session}
                icon={<IoAnalytics size={20} />}
                status="neutral"
              />
            </CompactCardGrid>

            <div className="growth-events-grid">
              {/* Events by Type */}
              <div className="growth-events-section">
                <h4>Events by Type</h4>
                <div className="growth-events-list">
                  {events.events_by_type.slice(0, 8).map((e) => (
                    <div key={e.event} className="growth-event-item">
                      <span className="growth-event-name">{e.event}</span>
                      <span className="growth-event-count">{e.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Pages */}
              <div className="growth-events-section">
                <h4>Top Pages</h4>
                <div className="growth-events-list">
                  {events.top_pages.slice(0, 8).map((p) => (
                    <div key={p.page} className="growth-event-item">
                      <IoEye size={14} />
                      <span className="growth-event-name">{p.page || '/'}</span>
                      <span className="growth-event-count">{p.views.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Referrers */}
              <div className="growth-events-section">
                <h4>Top Referrers</h4>
                <div className="growth-events-list">
                  {events.top_referrers.slice(0, 8).map((r) => (
                    <div key={r.referrer} className="growth-event-item">
                      <span className="growth-event-name">{r.referrer}</span>
                      <span className="growth-event-count">{r.sessions} sessions</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </AccordionSection>
    </div>
  )
}
